import { getProjects, getProjectApiKeys } from './endpoints';
import type { ApiKey, ApiResponse, Project } from './types';
import { METADATA_RESOURCE_TTL_MS, readProviderResource, withProviderResourceLock } from '../provider-resource-cache';

interface OpenAiMetadata {
  projects: Project[];
  allApiKeys: ApiKey[];
  partialErrors: string[];
  freshUntil: number;
}

/** Cache inventory independently of dates, and retain successful projects when
 * another project fails. Workers cap the fan-out at four; each resource is shared
 * by the dashboard, monitor and (where Web Locks exist) other tabs.
 */
export const getOpenAiMetadata = async (
  apiKey: string,
  credentialRevision: string,
  onProgress?: (message: string) => void,
  forceRefresh = false
): Promise<ApiResponse<OpenAiMetadata>> => withProviderResourceLock(
  `openai:metadata:${credentialRevision}`,
  () => loadMetadata(apiKey, credentialRevision, onProgress, forceRefresh),
  forceRefresh
);

const loadMetadata = async (
  apiKey: string,
  credentialRevision: string,
  onProgress: ((message: string) => void) | undefined,
  forceRefresh: boolean
): Promise<ApiResponse<OpenAiMetadata>> => {
  onProgress?.('Loading project and API key metadata...');
  const projectSnapshot = await readProviderResource({
    service: 'openai', endpoint: 'projects_v2', params: { includeArchived: 'true' },
    credentialRevision, ttl: METADATA_RESOURCE_TTL_MS, forceRefresh,
    load: () => getProjects(apiKey, true),
    isComplete: response => response.success && Array.isArray(response.data?.data)
  });
  const response = projectSnapshot.value;
  if (!response.success || !response.data) {
    return { success: false, error: response.error || 'Failed to fetch projects' };
  }
  const projects = response.data.data;
  const activeProjects = projects.filter(project => project.status !== 'archived');
  const keysByProject: ApiKey[][] = new Array(activeProjects.length);
  const partialErrors: string[] = [];
  let freshUntil = projectSnapshot.freshUntil;
  let nextProject = 0;
  await Promise.all(Array.from({ length: Math.min(4, activeProjects.length) }, async () => {
    while (nextProject < activeProjects.length) {
      const index = nextProject++;
      const project = activeProjects[index];
      try {
        const snapshot = await readProviderResource({
          service: 'openai', endpoint: 'project_keys_v1', params: { projectId: project.id },
          credentialRevision, ttl: METADATA_RESOURCE_TTL_MS, forceRefresh,
          load: () => getProjectApiKeys(apiKey, project.id),
          isComplete: result => result.success && Array.isArray(result.data?.data)
        });
        freshUntil = Math.min(freshUntil, snapshot.freshUntil);
        if (!snapshot.value.success || !snapshot.value.data) {
          throw new Error(snapshot.value.error || 'request failed');
        }
        keysByProject[index] = snapshot.value.data.data.map(key => ({
          ...key, projectId: project.id, projectName: project.name
        }));
      } catch (error) {
        keysByProject[index] = [];
        partialErrors.push(`API key metadata unavailable for project ${project.id}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }
  }));
  return { success: true, data: { projects, allApiKeys: keysByProject.flat(), partialErrors, freshUntil } };
};
